import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, api } from './api/client';
import type {
  AuthStatusResponse,
  BriefInput,
  FormPurpose,
  ConfigResponse,
  DriveSearchResponse,
  ExtractionResult,
  TrackedRequest,
  RolloutAction,
  RolloutDraft,
  SubmitRolloutResponse,
  ValidationIssue,
} from './types';
import { AppShell, type Step } from './components/layout/AppShell';
import { Card, Notice, Spinner } from './components/ui/primitives';
import { DashboardScreen } from './screens/DashboardScreen';
import { RequestsScreen } from './screens/RequestsScreen';
import { StartScreen } from './screens/StartScreen';
import { ReviewEditScreen } from './screens/ReviewEditScreen';
import { DestinationScreen } from './screens/DestinationScreen';
import { ErrorPanel, ProcessingScreen, SuccessScreen } from './screens/StatusScreens';
import { emptyBriefInput } from './state/draft';
import { clearBriefInput, loadBriefInput } from './state/briefInputStorage';
import { useStepRouting } from './state/routing';

/**
 * Flow controller.
 *
 * Start (sign in + paste)  ->  /form  ->  /destination  ->  /processing  ->  /success
 *
 * All state for the run lives here so a step can be revisited without losing
 * the user's edits, and the submit action is guarded against double firing.
 */

const APP_TITLE = 'Audience Push Request';

/** Reads the one-shot query parameters the OAuth callback redirects with. */
function readAuthRedirect(): { error: string | null; email: string | null; connected: boolean } {
  const params = new URLSearchParams(window.location.search);
  const result = {
    error: params.get('auth_error'),
    email: params.get('email'),
    connected: params.get('connected') === '1',
  };

  // Clear them so a refresh does not replay the message.
  if (result.error || result.connected) {
    window.history.replaceState({}, '', window.location.pathname);
  }
  return result;
}

/** Mirrors the server's tab title format for the destination preview. */
function tabTitlePreview(channel: string): string {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${now.getFullYear()} - ${channel.toUpperCase()}`;
}

export default function App() {
  // --- Bootstrap ---------------------------------------------------------
  const [auth, setAuth] = useState<AuthStatusResponse | null>(null);
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [booting, setBooting] = useState(true);
  const [bootError, setBootError] = useState<string | null>(null);
  const authRedirect = useRef(readAuthRedirect());

  // --- Flow state --------------------------------------------------------
  const [step, setStep] = useState<Step>('dashboard');
  // Restores anything typed before a sign-in redirect took the page away.
  const [briefInput, setBriefInput] = useState<BriefInput>(
    () => loadBriefInput() ?? emptyBriefInput(),
  );
  const [draft, setDraft] = useState<RolloutDraft | null>(null);
  /*
   * Why the form is open. Creating is the default; viewing, amending and
   * duplicating all reuse the same screen, because one rendering of a rollout
   * cannot disagree with itself.
   */
  const [purpose, setPurpose] = useState<FormPurpose>({ mode: 'create' });
  /*
   * The combination whose tabs are open. Held here so that opening a request in
   * the form and coming back returns to those tabs, not to the top of the list.
   */
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);
  const [parseNotes, setParseNotes] = useState<string[]>([]);
  const [search, setSearch] = useState<DriveSearchResponse | null>(null);
  const [result, setResult] = useState<SubmitRolloutResponse | null>(null);

  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /**
   * Guards against a duplicate submission: the key is minted once per attempt
   * and the backend replays the original result if the same key arrives twice.
   */
  const idempotencyKey = useRef<string | null>(null);
  const submitting = useRef(false);

  /** A step is reachable only when the in-memory data it renders exists. */
  const canEnter = useCallback(
    (target: Step) => {
      switch (target) {
        case 'dashboard':
        case 'requests':
        case 'input':
          return true;
        case 'review':
          return Boolean(draft);
        case 'destination':
          return Boolean(draft && search);
        case 'success':
          return Boolean(result);
        case 'processing':
          // Never restorable by navigation — it represents work in flight.
          return false;
      }
    },
    [draft, search, result],
  );

  useStepRouting(step, setStep, canEnter);

  const refreshAuth = useCallback(async () => {
    try {
      setAuth(await api.getAuthStatus());
    } catch {
      // A failed status check is not fatal; treat it as "not connected".
      setAuth({ connected: false, email: null, name: null, picture: null, authorized: false });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [status, appConfig] = await Promise.all([api.getAuthStatus(), api.getConfig()]);
        if (cancelled) return;
        setAuth(status);
        setConfig(appConfig);
      } catch (err) {
        if (cancelled) return;
        setBootError(
          err instanceof ApiError ? err.message : 'Could not load the application configuration.',
        );
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleConnect = useCallback(() => api.startGoogleConnect(), []);

  const resetFlow = useCallback(() => {
    setStep('dashboard');
    setBriefInput(emptyBriefInput());
    clearBriefInput();
    setDraft(null);
    setPurpose({ mode: 'create' });
    setOpenGroupKey(null);
    setParseNotes([]);
    setSearch(null);
    setResult(null);
    setIssues([]);
    setError(null);
    idempotencyKey.current = null;
    submitting.current = false;
  }, []);

  const handleSignOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    await refreshAuth();
    // A different account may have different Drive access, so start clean.
    resetFlow();
  }, [refreshAuth, resetFlow]);

  /**
   * Turns a thrown ApiError into screen state; validation issues go inline.
   *
   * A rejected request carries the fields that failed, whether it was rejected
   * by the business rules (`VALIDATION_FAILED`) or by the request schema
   * (`INVALID_REQUEST`). Both are shown. Dropping the schema ones left the user
   * staring at "the request was not in the expected shape" with no way to tell
   * which field caused it.
   */
  const handleFailure = useCallback(
    (err: unknown) => {
      if (err instanceof ApiError) {
        if (
          (err.code === 'VALIDATION_FAILED' || err.code === 'INVALID_REQUEST') &&
          err.issues.length
        ) {
          setIssues(err.issues);
          // A schema rejection is a bug rather than something the user typed
          // wrongly, so the banner stays up alongside the field list.
          setError(err.code === 'INVALID_REQUEST' ? err : null);
          return;
        }
        setError(err);
        if (err.needsGoogle) void refreshAuth();
        return;
      }
      setError(new ApiError('INTERNAL_ERROR', 'Something went wrong. Please try again.', 500));
    },
    [refreshAuth],
  );


  /**
   * Opens an existing request in the rollout form.
   *
   * All three modes load the same stored draft; only the outcome differs. A
   * duplicate deliberately gets a **fresh idempotency key** — reusing the
   * original's would make the backend replay that submission and hand back the
   * first document instead of creating a new tab.
   */
  const handleOpenForm = useCallback(
    async (entry: TrackedRequest, mode: 'view' | 'amend' | 'duplicate') => {
      setBusy(true);
      setIssues([]);
      setError(null);

      try {
        const detail = await api.getRequest(entry.id);

        setDraft(detail.draft);
        setParseNotes([]);
        setSearch(null);
        setResult(null);
        idempotencyKey.current = null;
        submitting.current = false;

        setPurpose(
          mode === 'duplicate'
            ? { mode: 'duplicate', fromTabTitle: entry.tabTitle }
            : { mode, requestId: entry.id, tabTitle: entry.tabTitle },
        );
        setStep('review');
      } catch (err) {
        handleFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [handleFailure],
  );

  /** Rewrites the tab of the request being amended. */
  const handleAmend = useCallback(async () => {
    if (!draft || purpose.mode !== 'amend') return;

    setBusy(true);
    setIssues([]);
    setError(null);

    try {
      await api.amendRequest(purpose.requestId, draft);

      /*
       * Back to the tabs the edit was started from, with the combination still
       * open. The screen reloads on mount, so it shows the amended request —
       * and the operator lands where they were rather than at the top of the
       * list, hunting for the row they just edited.
       */
      setDraft(null);
      setPurpose({ mode: 'create' });
      setStep('requests');
    } catch (err) {
      handleFailure(err);
    } finally {
      setBusy(false);
    }
  }, [draft, purpose, handleFailure]);

  // --- Start -> /form ----------------------------------------------------
  const handleParse = useCallback(
    async (input: BriefInput, attachments: ExtractionResult[]) => {
      setBusy(true);
      setIssues([]);
      setError(null);
      setBriefInput(input);

      try {
        const response = await api.parseBrief({ ...input, attachments });
        setDraft(response.draft);
        setParseNotes(response.notes);
        setStep('review');
      } catch (err) {
        handleFailure(err);
      } finally {
        setBusy(false);
      }
    },
    [handleFailure],
  );

  // --- /form -> /destination --------------------------------------------
  const handleReviewContinue = useCallback(async () => {
    if (!draft) return;

    setBusy(true);
    setIssues([]);
    setError(null);

    try {
      // Validate before spending a Drive round trip.
      const validation = await api.validateDraft(draft);
      if (!validation.valid) {
        setIssues(validation.issues);
        return;
      }

      const response = await api.searchDrive({
        businessType: draft.businessType,
        agencyName: draft.record.agencyName,
        brandName: draft.record.brandName,
      });

      setSearch(response);
      // Warnings from validation stay visible on the destination step.
      setIssues(validation.issues.filter((issue) => issue.severity === 'warning'));
      setStep('destination');
    } catch (err) {
      handleFailure(err);
    } finally {
      setBusy(false);
    }
  }, [draft, handleFailure]);

  // --- /destination -> /processing -> /success ---------------------------
  const handleSubmit = useCallback(
    async (action: RolloutAction, documentId?: string) => {
      if (!draft) return;
      // A second click while the first request is in flight must do nothing.
      if (submitting.current) return;

      submitting.current = true;
      idempotencyKey.current ??= `${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 12)}`;

      setBusy(true);
      setError(null);
      setStep('processing');

      try {
        const response = await api.submitRollout({
          draft,
          action,
          ...(documentId ? { documentId } : {}),
          originalBrief: briefInput.previousBrief,
          idempotencyKey: idempotencyKey.current,
        });

        setResult(response);
        setStep('success');
      } catch (err) {
        // Back to the destination screen, where the error and the choices are
        // both visible, rather than stranding the user on a spinner.
        setStep('destination');
        // A fresh key: the failed attempt did not create anything to replay.
        idempotencyKey.current = null;
        handleFailure(err);
      } finally {
        submitting.current = false;
        setBusy(false);
      }
    },
    [draft, briefInput.previousBrief, handleFailure],
  );

  // --- Render ------------------------------------------------------------

  const errorPanel = useMemo(() => {
    if (!error) return null;
    return (
      <ErrorPanel
        title={errorTitle(error)}
        message={error.message}
        issues={error.code === 'INVALID_REQUEST' ? error.issues : []}
        needsGoogle={error.needsGoogle}
        onConnect={handleConnect}
        onRetry={
          error.code === 'NETWORK_ERROR' || error.code === 'GOOGLE_API_DISABLED'
            ? () => setError(null)
            : undefined
        }
      />
    );
  }, [error, handleConnect]);

  if (booting) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <Spinner className="h-6 w-6 text-accent" />
      </div>
    );
  }

  if (bootError || !config) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
        <div className="w-full max-w-md">
          <Notice tone="error" title="The application could not start">
            {bootError ?? 'Configuration is unavailable.'}
          </Notice>
        </div>
      </div>
    );
  }

  return (
    <AppShell
      title={APP_TITLE}
      step={step}
      auth={auth}
      onConnect={handleConnect}
      onDisconnect={handleSignOut}
      onNavigate={(section) => {
        setIssues([]);
        setError(null);

        /*
         * Leaving the create flow half-finished discards it, rather than leaving
         * a stale draft that a later step could pick up and file under the wrong
         * brand. The dashboard and the tracking list both start clean.
         */
        if (section === 'dashboard') {
          resetFlow();
          setStep('dashboard');
          return;
        }

        setDraft(null);
        setPurpose({ mode: 'create' });
        setStep('requests');
      }}
    >
      <div className="space-y-5">
        {step !== 'processing' ? errorPanel : null}

        {step === 'dashboard' ? (
          <DashboardScreen onOpenRequests={() => setStep('requests')} />
        ) : null}

        {step === 'requests' ? (
          <RequestsScreen
            openKey={openGroupKey}
            onOpenKeyChange={setOpenGroupKey}
            onOpenForm={handleOpenForm}
            onNewRequest={() => {
              /*
               * A new request starts from a clean slate: leftover state from the
               * last one is the fastest way to file a rollout under the wrong
               * brand.
               */
              setBriefInput(emptyBriefInput());
              clearBriefInput();
              setDraft(null);
              setParseNotes([]);
              setSearch(null);
              setResult(null);
              setIssues([]);
              setError(null);
              idempotencyKey.current = null;
              submitting.current = false;
              setPurpose({ mode: 'create' });
              setStep('input');
            }}
          />
        ) : null}

        {step === 'input' ? (
          <StartScreen
            initial={briefInput}
            auth={auth}
            authError={authRedirect.current.error}
            authErrorEmail={authRedirect.current.email}
            serverIssues={issues}
            submitting={busy}
            onConnect={handleConnect}
            onSignOut={handleSignOut}
            onContinue={handleParse}
          />
        ) : null}

        {step === 'review' && draft ? (
          <ReviewEditScreen
            draft={draft}
            config={config}
            parseNotes={parseNotes}
            serverIssues={issues}
            submitting={busy}
            purpose={purpose}
            onChange={setDraft}
            onBack={() => {
              setIssues([]);
              setError(null);

              /*
               * A form opened from the tracking list goes back to the tabs it was
               * opened from — the combination stays open, so the operator returns
               * to where they were rather than to the top of the list.
               */
              if (purpose.mode === 'create') {
                setStep('input');
                return;
              }

              setDraft(null);
              setPurpose({ mode: 'create' });
              setStep('requests');
            }}
            onContinue={purpose.mode === 'amend' ? handleAmend : handleReviewContinue}
          />
        ) : null}

        {step === 'destination' && draft && search ? (
          <DestinationScreen
            draft={draft}
            search={search}
            tabTitlePreview={tabTitlePreview(draft.integration.channel)}
            submitting={busy}
            onBack={() => {
              setIssues([]);
              setStep('review');
            }}
            onSubmit={handleSubmit}
          />
        ) : null}

        {step === 'processing' ? <ProcessingScreen /> : null}

        {step === 'success' && result ? (
          <SuccessScreen result={result} onStartAnother={resetFlow} />
        ) : null}

        {/* Defensive: a step whose data went missing should offer a way out. */}
        {(step === 'review' && !draft) ||
        (step === 'destination' && (!draft || !search)) ||
        (step === 'success' && !result) ? (
          <Card className="p-6">
            <Notice tone="warning" title="This step lost its data">
              Start the brief again to continue.
              <button
                type="button"
                onClick={resetFlow}
                className="ml-2 font-medium text-accent hover:underline"
              >
                Start over
              </button>
            </Notice>
          </Card>
        ) : null}
      </div>
    </AppShell>
  );
}

function errorTitle(error: ApiError): string {
  switch (error.code) {
    case 'NOT_AUTHENTICATED':
    case 'GOOGLE_AUTH_EXPIRED':
      return 'Google Drive needs reconnecting';
    case 'GOOGLE_PERMISSION_DENIED':
      return 'Permission denied';
    case 'GOOGLE_API_DISABLED':
      return 'A required Google API is not enabled';
    case 'FOLDER_NOT_FOUND':
      return 'Drive folder not found';
    case 'DOCUMENT_NOT_FOUND':
      return 'Document not found';
    case 'GOOGLE_RATE_LIMITED':
      return 'Google is rate-limiting us';
    case 'NETWORK_ERROR':
      return 'Connection problem';
    case 'NOT_CONFIGURED':
      return 'Server not configured';
    case 'DUPLICATE_SUBMISSION':
      return 'Already submitted';
    default:
      return 'Something went wrong';
  }
}
